import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import ModalScrim from "./ModalScrim";
import DataRowTable from "./DataRowTable";
import RowActionBar from "./RowActionBar";
import { createProgram, deleteProgram, updateProgram } from "../lib/api";
import { programAnalyticsHref } from "../lib/programFlows";

function ProgramFormModal({ mode, program, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(program?.name || "");
  const [objective, setObjective] = useState(program?.business_objective || program?.description || "");
  const [sponsor, setSponsor] = useState(program?.executive_sponsor || "");
  const [programManager, setProgramManager] = useState(program?.program_manager || "");
  const [lifecycle, setLifecycle] = useState(program?.lifecycle || program?.status || "Initiation");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (isEdit) {
        await updateProgram(program.id, {
          name,
          objective,
          sponsor,
          program_manager: programManager,
          lifecycle,
          status: lifecycle,
        });
      } else {
        await createProgram({ name, objective, sponsor });
      }
      onSaved();
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalScrim testId={isEdit ? "program-edit-modal" : "program-create-modal"}>
      <form className="modal wide" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <p className="kicker">{isEdit ? "MODIFY PROGRAM" : "NEW PROGRAM"}</p>
        <h2>{isEdit ? `Edit ${program?.id}` : "Add new program"}</h2>
        {isEdit && <p className="muted small-note">{program?.id}</p>}
        <label>Name<input data-testid="program-form-name" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Objective<textarea data-testid="program-form-objective" required rows={3} value={objective} onChange={(e) => setObjective(e.target.value)} /></label>
        <label>Executive sponsor<input data-testid="program-form-sponsor" required value={sponsor} onChange={(e) => setSponsor(e.target.value)} /></label>
        {isEdit && (
          <>
            <label>Program manager<input data-testid="program-form-pm" value={programManager} onChange={(e) => setProgramManager(e.target.value)} /></label>
            <label>Lifecycle
              <select data-testid="program-form-lifecycle" value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
                {["Initiation", "Planning", "Execution", "Closing", "Closed"].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </label>
          </>
        )}
        {err && <div className="error-message">{err}</div>}
        <button className="primary-button full" type="submit" disabled={busy} data-testid="program-form-submit">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create program"}
        </button>
      </form>
    </ModalScrim>
  );
}

const PROGRAM_COLUMNS = [
  {
    key: "name",
    label: "Program",
    render: (row) => (
      <Link to={programAnalyticsHref(row.id)} className="program-name-link" data-testid={`program-name-${row.id}`}>
        {row.name}
      </Link>
    ),
  },
  { key: "id", label: "ID", render: (row) => <code>{row.id}</code> },
  { key: "lifecycle", label: "Lifecycle", render: (row) => row.lifecycle || row.status || "—" },
  { key: "apps", label: "Apps", render: (row) => row.summary?.applications ?? 0 },
  { key: "mdr", label: "MDR", render: (row) => row.summary?.approved ?? 0 },
];

export default function ProgramListDirectory({ programs, canCreate, onRefresh, onViewProgram }) {
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  const handleView = (row) => {
    if (onViewProgram) onViewProgram(row.id);
    else window.location.assign(programAnalyticsHref(row.id));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteErr("");
    try {
      await deleteProgram(deleteTarget.id);
      setDeleteTarget(null);
      onRefresh();
    } catch (ex) {
      setDeleteErr(ex.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <section className="feature-panel program-list-panel" data-testid="program-list program-list-directory">
        <div className="panel-header program-list-header">
          <div>
            <p className="kicker">PROGRAM DIRECTORY</p>
            <h2>All programs</h2>
          </div>
          {canCreate && (
            <button
              type="button"
              className="primary-button program-add-btn"
              data-testid="program-add-new"
              onClick={() => setModal({ mode: "create" })}
            >
              <Plus size={16} /> Add new program
            </button>
          )}
        </div>

        <DataRowTable
          testId="program-directory-table"
          columns={PROGRAM_COLUMNS}
          rows={programs}
          rowKey="id"
          pageSize={10}
          emptyMessage={canCreate ? "No programs yet. Use Add new program to create one." : "No programs yet."}
          hint="Always-visible Actions on each row — View, Modify and Delete (when permitted)."
          renderActions={(row, stableId) => {
            const deletable = canCreate && row.created_in_product && !(row.summary?.applications > 0);
            const editable = canCreate;
            return (
              <RowActionBar
                testId={`program-actions-${stableId}`}
                rowId={stableId}
                view
                modify={editable}
                delete={deletable}
                onView={() => handleView(row)}
                onModify={() => setModal({ mode: "edit", program: row })}
                onDelete={() => setDeleteTarget(row)}
              />
            );
          }}
        />
      </section>

      {modal && (
        <ProgramFormModal
          mode={modal.mode}
          program={modal.program}
          onClose={() => setModal(null)}
          onSaved={onRefresh}
        />
      )}

      {deleteTarget && (
        <ModalScrim testId="program-delete-modal">
          <div className="modal">
            <p className="kicker">DELETE PROGRAM</p>
            <h2>Delete {deleteTarget.name}?</h2>
            <p className="muted">This removes {deleteTarget.id} from the tenant. Applications must be moved first.</p>
            {deleteErr && <div className="error-message">{deleteErr}</div>}
            <div className="login-actions">
              <button type="button" className="secondary-button" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</button>
              <button type="button" className="primary-button danger-button" data-testid="program-delete-confirm" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? "Deleting…" : "Delete program"}
              </button>
            </div>
          </div>
        </ModalScrim>
      )}
    </>
  );
}
